/**
 * The single path every request to the backend takes.
 *
 * Three things it exists to centralise:
 *  - the authentication cookie is HttpOnly, so `credentials: 'include'` must be
 *    set on every call and there is no token for the client to manage;
 *  - mutating calls need the antiforgery header echoed from the readable
 *    XSRF-TOKEN cookie (see AntiforgeryController);
 *  - a 401 means the session is gone, and the app has to fall back to the login
 *    screen from wherever it happens to be.
 */

/** Header the backend validates; set via AddAntiforgery in Program.cs. */
const XSRF_HEADER = 'RequestVerificationToken';
const XSRF_COOKIE = 'XSRF-TOKEN';

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * A non-2xx response, carrying the parsed ProblemDetails body when there was
 * one. Program.cs configures ProblemDetails for the whole /api surface, so
 * callers can rely on this shape rather than sniffing the body themselves.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | null;

  constructor(status: number, problem: ProblemDetails | null, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  /**
   * Per-field validation messages, as produced by [ApiController] model-state
   * failures. Shaped for React Hook Form's `setError`.
   */
  get fieldErrors(): Record<string, string[]> {
    return this.problem?.errors ?? {};
  }
}

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  errors?: Record<string, string[]>;
}

/** Invoked when the server reports the session is gone. Set by AuthProvider. */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Asks the backend to mint the antiforgery cookie pair. Call once at startup and
 * again after login: the token is bound to the user identity, so one minted
 * while anonymous stops validating the moment the user signs in.
 */
export async function primeAntiforgeryToken(): Promise<void> {
  await fetch('/api/antiforgery/token', { credentials: 'include' });
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Serialised as JSON unless it is already a FormData or a string. */
  body?: unknown;
  /** Appended as a query string, skipping null and undefined values. */
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Suppresses the global 401 handling, for probes like GET /api/auth/me. */
  skipUnauthorizedHandler?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  if (!query) return path;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== null && value !== undefined) params.append(key, String(value));
  }

  const qs = params.toString();
  return qs ? `${path}${path.includes('?') ? '&' : '?'}${qs}` : path;
}

async function parseProblem(response: Response): Promise<ProblemDetails | null> {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) return null;

  try {
    return (await response.json()) as ProblemDetails;
  } catch {
    return null;
  }
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, query, skipUnauthorizedHandler, headers, ...rest } = options;
  const method = (rest.method ?? 'GET').toUpperCase();

  const requestHeaders = new Headers(headers);
  let requestBody: BodyInit | undefined;

  if (body !== undefined) {
    if (body instanceof FormData || typeof body === 'string') {
      requestBody = body;
    } else {
      requestBody = JSON.stringify(body);
      requestHeaders.set('Content-Type', 'application/json');
    }
  }

  if (MUTATING_METHODS.has(method)) {
    const token = readCookie(XSRF_COOKIE);
    if (token) requestHeaders.set(XSRF_HEADER, token);
  }

  const response = await fetch(buildUrl(path, query), {
    ...rest,
    method,
    headers: requestHeaders,
    body: requestBody,
    credentials: 'include',
  });

  if (response.status === 401 && !skipUnauthorizedHandler) {
    onUnauthorized?.();
  }

  if (!response.ok) {
    const problem = await parseProblem(response);
    throw new ApiError(
      response.status,
      problem,
      problem?.detail ?? problem?.title ?? `Request failed with status ${response.status}`,
    );
  }

  // 204 is the success shape for every command endpoint on this API.
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    return (await response.text()) as T;
  }

  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => apiFetch<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
};
